import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AudioCard from "@/components/AudioCard";
import { Upload, X, Plus, ListMusic, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { usePlayer } from "@/context/PlayerContext";

export default function Music() {
  const [tracks, setTracks] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const [activePlaylist, setActivePlaylist] = useState(null);
  const { playTrack } = usePlayer();

  const load = async () => {
    const [t, p] = await Promise.all([api.get("/audios"), api.get("/playlists")]);
    setTracks(t.data);
    setPlaylists(p.data);
  };

  useEffect(() => { load(); }, []);

  const openPlaylist = async (pid) => {
    const { data } = await api.get(`/playlists/${pid}`);
    setActivePlaylist(data);
  };

  const addToPlaylist = async (pid, audio_id) => {
    try {
      await api.post(`/playlists/${pid}/tracks`, { audio_id });
      toast.success("Added to playlist");
      if (activePlaylist?.id === pid) openPlaylist(pid);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeFromPlaylist = async (pid, audio_id) => {
    try {
      await api.delete(`/playlists/${pid}/tracks/${audio_id}`);
      openPlaylist(pid);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deletePlaylist = async (pid) => {
    try {
      await api.delete(`/playlists/${pid}`);
      setActivePlaylist(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#ff4500] font-mono">── Music</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-3 tracking-tighter">Sound library</h1>
          <p className="text-[#a3a3a3] mt-3 text-sm">MP3 tracks, playlists and now-playing.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPlaylistOpen(true)} data-testid="open-playlist-create"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#ff4500] text-xs uppercase tracking-[0.2em]">
            <Plus className="w-4 h-4" /> Playlist
          </button>
          <button onClick={() => setUploadOpen(true)} data-testid="open-audio-upload"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#ff4500] hover:bg-[#ff5722] text-white text-xs uppercase tracking-[0.2em]">
            <Upload className="w-4 h-4" /> Upload
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Playlists sidebar */}
        <aside className="lg:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] font-mono mb-3">// Playlists</div>
          <div className="space-y-1">
            <button
              onClick={() => setActivePlaylist(null)}
              data-testid="playlist-all"
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border ${!activePlaylist ? "border-[#ff4500]/40 bg-[#1a1a1a]" : "border-transparent hover:bg-[#141414]"}`}
            >
              <ListMusic className="w-4 h-4" /> All tracks
              <span className="ml-auto text-[10px] text-[#a3a3a3] font-mono">{tracks.length}</span>
            </button>
            {playlists.map((p) => (
              <button
                key={p.id}
                onClick={() => openPlaylist(p.id)}
                data-testid={`playlist-${p.id}`}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border ${activePlaylist?.id === p.id ? "border-[#ff4500]/40 bg-[#1a1a1a]" : "border-transparent hover:bg-[#141414]"}`}
              >
                <ListMusic className="w-4 h-4" /> <span className="truncate">{p.name}</span>
                <span className="ml-auto text-[10px] text-[#a3a3a3] font-mono">{p.track_ids?.length || 0}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Main */}
        <section className="lg:col-span-9">
          {activePlaylist ? (
            <div>
              <div className="flex items-end justify-between mb-5">
                <div>
                  <h2 className="font-display text-3xl tracking-tight">{activePlaylist.name}</h2>
                  <p className="text-sm text-[#a3a3a3] mt-1">{activePlaylist.description || "No description"}</p>
                </div>
                <button onClick={() => deletePlaylist(activePlaylist.id)} data-testid="delete-playlist-btn"
                  className="text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#ff0033] inline-flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>

              {activePlaylist.tracks?.length ? (
                <div className="border border-[#2a2a2a]">
                  {activePlaylist.tracks.map((t, idx) => (
                    <div key={t.id} className="flex items-center justify-between px-4 py-3 border-b border-[#2a2a2a] last:border-b-0 hover:bg-[#141414]">
                      <button onClick={() => playTrack(t, activePlaylist.tracks)} className="flex-1 text-left flex items-center gap-3" data-testid={`playlist-track-${t.id}`}>
                        <span className="text-[10px] font-mono text-[#a3a3a3] w-6">{String(idx + 1).padStart(2, "0")}</span>
                        <div>
                          <div className="text-sm font-display">{t.title}</div>
                          <div className="text-xs text-[#a3a3a3]">{t.artist}</div>
                        </div>
                      </button>
                      <button onClick={() => removeFromPlaylist(activePlaylist.id, t.id)} className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#ff0033]">Remove</button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border border-dashed border-[#2a2a2a] p-10 text-center text-sm text-[#a3a3a3]">
                  Empty playlist. Add tracks from the library.
                </div>
              )}

              <div className="mt-10">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#ff4500] font-mono mb-3">── Add tracks</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {tracks.filter((t) => !activePlaylist.track_ids?.includes(t.id)).map((t) => (
                    <button key={t.id} onClick={() => addToPlaylist(activePlaylist.id, t.id)} data-testid={`add-to-playlist-${t.id}`}
                      className="text-left bg-[#141414] border border-[#2a2a2a] hover:border-[#ff4500] p-3 transition-colors">
                      <div className="text-sm truncate font-display">{t.title}</div>
                      <div className="text-xs text-[#a3a3a3] truncate">{t.artist}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            tracks.length ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {tracks.map((t) => <AudioCard key={t.id} audio={t} queue={tracks} />)}
              </div>
            ) : (
              <div className="border border-dashed border-[#2a2a2a] p-16 text-center text-sm text-[#a3a3a3]">
                No tracks yet. Upload your first MP3.
              </div>
            )
          )}
        </section>
      </div>

      {uploadOpen && <UploadAudioDialog onClose={() => setUploadOpen(false)} onDone={() => { setUploadOpen(false); load(); }} />}
      {playlistOpen && <NewPlaylistDialog onClose={() => setPlaylistOpen(false)} onDone={() => { setPlaylistOpen(false); load(); }} />}
    </div>
  );
}

function UploadAudioDialog({ onClose, onDone }) {
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [category, setCategory] = useState("music");
  const [cover, setCover] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Choose an MP3"); return; }
    if (!file.name.toLowerCase().endsWith(".mp3")) { setErr("Only MP3 files allowed"); return; }
    setErr(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("title", title); fd.append("artist", artist); fd.append("category", category); fd.append("cover_url", cover);
      await api.post("/audios/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => setProgress(Math.round((p.loaded / (p.total || 1)) * 100)),
      });
      toast.success("Track uploaded"); onDone();
    } catch (e2) { setErr(formatApiError(e2)); } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#141414] border border-[#2a2a2a] reveal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2a2a]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] font-mono">// Upload track</div>
          <button onClick={onClose} className="text-[#a3a3a3] hover:text-[#f5f5f5]" data-testid="close-audio-upload"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div><label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Title</label>
            <input required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="audio-title-input"
              className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm" /></div>
          <div><label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Artist</label>
            <input value={artist} onChange={(e) => setArtist(e.target.value)} data-testid="audio-artist-input"
              className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm" /></div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Culture category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="audio-category-input"
              className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm appearance-none"
            >
              <option value="music">Music & Dance · Mizik</option>
              <option value="history">History · Istwa Ayiti</option>
              <option value="carnival">Carnival & Festivals · Kanaval</option>
              <option value="cuisine">Cuisine · Manje Ayisyen</option>
              <option value="arts">Arts & Crafts · Atizay</option>
              <option value="geography">Geography · Jewografi</option>
              <option value="general">General · Tout sa ki gen</option>
            </select>
          </div>
          <div><label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Cover URL (optional)</label>
            <input value={cover} onChange={(e) => setCover(e.target.value)} placeholder="https://…" data-testid="audio-cover-input"
              className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm" /></div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">MP3 file (max 100MB)</label>
            <div className="mt-2 border border-dashed border-[#2a2a2a] p-4 flex items-center justify-between">
              <span className="text-xs text-[#a3a3a3] truncate" data-testid="audio-file-label">{file ? file.name : "No file chosen"}</span>
              <button type="button" onClick={() => inputRef.current?.click()} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#ff4500]">Choose</button>
              <input ref={inputRef} type="file" accept="audio/mpeg,audio/mp3,.mp3" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="audio-file-input" />
            </div>
          </div>
          {uploading && (<div><div className="h-1 bg-[#2a2a2a]"><div className="h-1 bg-[#ff4500]" style={{ width: `${progress}%` }} /></div><div className="text-[10px] font-mono text-[#a3a3a3] mt-2">{progress}%</div></div>)}
          {err && <div className="text-xs text-[#ff0033]" data-testid="audio-upload-error">{err}</div>}
          <button type="submit" disabled={uploading} data-testid="submit-audio-upload"
            className="w-full px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] disabled:opacity-50 text-white text-sm font-medium">
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </form>
      </div>
    </div>
  );
}

function NewPlaylistDialog({ onClose, onDone }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/playlists", { name, description });
      toast.success("Playlist created"); onDone();
    } catch (e2) { setErr(formatApiError(e2)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#141414] border border-[#2a2a2a]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2a2a]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] font-mono">// New playlist</div>
          <button onClick={onClose} className="text-[#a3a3a3] hover:text-[#f5f5f5]" data-testid="close-playlist-create"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" data-testid="playlist-name-input"
            className="w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm" />
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Description" data-testid="playlist-desc-input"
            className="w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm resize-none" />
          {err && <div className="text-xs text-[#ff0033]">{err}</div>}
          <button type="submit" data-testid="submit-playlist-create" className="w-full px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] text-white text-sm font-medium">Create</button>
        </form>
      </div>
    </div>
  );
}
